import { HouseIcon } from "@phosphor-icons/react/dist/csr/House";
import { ChartBarIcon } from "@phosphor-icons/react/dist/csr/ChartBar";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "@hicya/auth-sdk/react";
import { ErrorBoundary, Sidebar, useSidebarCollapse, useOnlineStatus } from "@hicya/ui";
import { hasBackendOverrides } from "@hicya/ui/backend";

import {
  SETTINGS_SECTIONS,
  getSettingsSection,
  settingsHref,
} from "@/components/settings/settings-navigation";
import { BottomNav } from "@/components/layout/bottomNav";
import { Header } from "@/components/layout/header";
import { useHabits } from "@/context/habits-context";
import { RouteLoading } from "@/components/layout/route-loading";
import { DatabaseGate, useDatabaseStatus } from "@/db/provider";
import { applyUpdate, onSWUpdate } from "@/pwa";
import { useSync } from "@/sync/provider";

import styles from "./appShell.module.scss";

import type { AuthState, DevIndicator, NavItem, SyncStatus } from "@hicya/ui";

const NAV_ITEMS: NavItem[] = [
  { id: "today", label: "Today", href: "/today", icon: <HouseIcon size={22} /> },
  { id: "stats", label: "Stats", href: "/stats", icon: <ChartBarIcon size={22} /> },
  { id: "settings", label: "Settings", href: "/settings", icon: <GearSixIcon size={22} /> },
];

function isNonProdOrigin(): boolean {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname.includes("-staging.");
}

function getDevIndicator(): DevIndicator | undefined {
  if (!isNonProdOrigin()) return undefined;
  return { active: hasBackendOverrides(), href: "/developer" };
}

function deriveActiveItem(pathname: string): string {
  if (pathname === "/" || pathname.startsWith("/today")) return "today";
  if (pathname.startsWith("/stats")) return "stats";
  if (pathname.startsWith("/settings")) return "settings";
  return "today";
}

function mapSyncStatus(syncState: string, isOnline: boolean): SyncStatus {
  if (!isOnline) return "offline";
  if (syncState === "syncing") return "syncing";
  return "synced";
}

export function AppShell() {
  const { collapsed, isMobile, toggle } = useSidebarCollapse({
    storageKey: "habits-sidebar-collapsed",
  });
  const { user, isAuthenticated, login, logout } = useAuth();
  const { state, initializationError, retryInitialization } = useHabits();
  const database = useDatabaseStatus();
  const { syncState, isOnline } = useSync();
  const appIsOnline = useOnlineStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    onSWUpdate((needsRefresh) => {
      if (needsRefresh) setUpdateAvailable(true);
    });
  }, []);

  const auth: AuthState =
    isAuthenticated && user
      ? {
          status: "signed-in",
          email: user.email,
          syncStatus: mapSyncStatus(syncState, isOnline && appIsOnline),
        }
      : { status: "signed-out" };

  const [settingsCollapsed, setSettingsCollapsed] = useState(false);
  const isSettings = location.pathname === "/settings";
  const activeItem = isSettings
    ? `settings-${getSettingsSection(location.search)}`
    : deriveActiveItem(location.pathname);
  const navItems = NAV_ITEMS.map((item) =>
    item.id !== "settings"
      ? item
      : {
          ...item,
          subItems: SETTINGS_SECTIONS.map((section) => ({
            ...section,
            id: `settings-${section.id}`,
            href: settingsHref(section.id),
          })),
          expanded: isSettings && !settingsCollapsed,
          onToggle: () => {
            if (!isSettings) {
              setSettingsCollapsed(false);
              void navigate("/settings");
            } else setSettingsCollapsed((value) => !value);
          },
        },
  );

  return (
    <div className={styles.shell} data-settings={isSettings ? "" : undefined}>
      {!isMobile && (
        <Sidebar
          app={{
            name: "Habits",
            icon: (
              <img
                src="/icons/brand-icon.png"
                alt=""
                width={36}
                height={36}
                className={styles.brandIcon}
              />
            ),
            accentColor: "var(--accent-foreground)",
          }}
          navItems={navItems}
          activeItem={activeItem}
          onNavigate={(href) => {
            setSettingsCollapsed(false);
            void navigate(href);
          }}
          auth={auth}
          onSignIn={() => void login({ provider: "google" })}
          onSignOut={() => void logout()}
          updateAvailable={updateAvailable}
          onUpdate={applyUpdate}
          collapsed={collapsed}
          onCollapseToggle={toggle}
          devIndicator={getDevIndicator()}
        />
      )}
      <div
        className={styles.main}
        data-collapsed={!isMobile && collapsed ? "" : undefined}
        data-no-sidebar={isMobile ? "" : undefined}
      >
        <Header />
        <main className={styles.content}>
          {initializationError ? (
            <div role="alert">
              <p>{initializationError}</p>
              <button type="button" onClick={retryInitialization}>
                Try again
              </button>
            </div>
          ) : database.status === "loading" ||
            (database.status === "ready" && state.phase === "loading") ? (
            <RouteLoading />
          ) : (
            <DatabaseGate>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </DatabaseGate>
          )}
        </main>
      </div>
      {isMobile && <BottomNav />}
    </div>
  );
}
